import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { i18n } from '../../../../i18n/index.js';
import { useMemo, useState, type ReactElement } from 'react';
import { ChevronDown, ChevronRight, ListTodo } from 'lucide-react';
import { Alert, Skeleton } from 'antd';

import { useTasks } from '../../../tasks/hooks/useTasks.js';
import { buildTaskKanbanColumns } from '../../../tasks/lib/taskKanban.js';
import { TaskKanbanBoard } from '../../../tasks/components/TaskKanbanBoard.js';
import { useTaskStatusMutation } from '../../../tasks/hooks/useTaskStatusMutation.js';
import { TaskFormModal } from '../../../tasks/components/TaskFormModal.js';
import type { TaskStatus } from '../../../tasks/api/tasks.client.js';

interface TaskKanbanChatSnippetProps {
  dealId: string;
}

/**
 * Live deal task kanban inside the chat. Rendered when the agent worked
 * with tasks (create/update/status/assign...): data via useTasks(dealId),
 * columns from the shared buildTaskKanbanColumns, drag&drop via
 * useTaskStatusMutation.
 */
export function TaskKanbanChatSnippet({ dealId }: TaskKanbanChatSnippetProps): ReactElement {
  useI18nSubscription();
  const [collapsed, setCollapsed] = useState(false);
  const [createStatus, setCreateStatus] = useState<TaskStatus | null>(null);

  const { data, isLoading, isError } = useTasks({ dealId });
  const statusMutation = useTaskStatusMutation();

  const columns = useMemo(() => buildTaskKanbanColumns(data ?? []), [data]);
  const total = columns.reduce((sum, column) => sum + column.count, 0);

  return (
    <div
      className="overflow-hidden rounded-xl border border-stone-200 bg-white"
      data-testid="task-kanban-chat"
    >
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left"
        aria-expanded={!collapsed}
        aria-label={i18n.t(
          collapsed ? 'crm.ai_chat.task_kanban.expand' : 'crm.ai_chat.task_kanban.collapse',
        )}
        onClick={() => setCollapsed((value) => !value)}
      >
        <ListTodo size={16} className="shrink-0 text-amber-600" aria-hidden="true" />
        <span className="text-sm font-semibold text-stone-800">
          {i18n.t('crm.ai_chat.task_kanban.title')}
        </span>
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-500">
          {total}
        </span>
        <span className="ml-auto text-stone-400" aria-hidden="true">
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {!collapsed &&
        (isLoading ? (
          <div className="px-4 pb-4">
            <Skeleton active title={false} paragraph={{ rows: 2 }} />
          </div>
        ) : isError ? (
          <div className="px-4 pb-4">
            <Alert type="error" showIcon title={i18n.t('crm.ai_chat.task_kanban.error')} />
          </div>
        ) : (
          <div className="px-3 pb-3">
            <TaskKanbanBoard
              columns={columns}
              isUpdating={statusMutation.isPending}
              onCreateInStatus={(status) => setCreateStatus(status)}
              onMoveTask={(taskId, toStatus) => statusMutation.mutate({ taskId, toStatus })}
            />
          </div>
        ))}

      {createStatus !== null && (
        <TaskFormModal
          open
          onClose={() => setCreateStatus(null)}
          initialStatus={createStatus}
          lockedDealId={dealId}
        />
      )}
    </div>
  );
}
